import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  Record,
} from 'react-admin'
import { IKSORIS_PARKING_TICKET_CASH_MACHINES_URL } from '../../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
  remove,
} from '../../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../../common/ticket-api-get-list.params'
import { IksorisParkingTicketCashMachineDto } from '../../../dto/device/ticket-machines/iksoris-parking-ticket-cash-machine/iksoris-parking-ticket-cash-machine.dto'
import mapSortParam from './iksoris-parking-ticket-cash-machine-sort.mapper'
import filterMapper from './iksoris-parking-ticket-cash-machine-filter.mapper'
import { CacheableDataProviderExtensionResult } from '../../../common/data-provider'
import { SetCredentialsDto } from '../../../dto/device/ticket-machines/automat-api/set-credentials.dto'
import { RemoveCredentialsDto } from '../../../dto/device/ticket-machines/automat-api/remove-credentials.dto'
import { SetAPIKeyDto } from '../../../dto/device/ticket-machines/set-api-key.dto'
import { RemoveAPIKeyDto } from '../../../dto/device/ticket-machines/remove-api-key.dto'
import { GetMoneyStateResponse } from '../../../dto/device/ticket-machines/automat-api/get-money-state-response'
import { ServiceCashPaymentRequest } from '../../../dto/device/ticket-machines/automat-api/service-cash-payment-request'
import { ServiceCashEmptyingRequest } from '../../../dto/device/ticket-machines/automat-api/service-cash-emptying-request'
import { TransactionResponse } from '../../../dto/device/ticket-machines/automat-api/transaction-response'
import { ServiceCashFloatRequest } from '../../../dto/device/ticket-machines/automat-api/service-cash-float-request'
import { CancelTransactionRequest } from '../../../dto/device/ticket-machines/automat-api/cancel-transaction-request'
import { FinishServicePaymentPartiallyRequest } from '../../../dto/device/ticket-machines/automat-api/finish-service-payment-partially-request'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<IksorisParkingTicketCashMachineDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<IksorisParkingTicketCashMachineDto[]>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<IksorisParkingTicketCashMachineDto>> => {
    const data = await get<IksorisParkingTicketCashMachineDto>(
      IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<IksorisParkingTicketCashMachineDto>> => {
    const data = await get<IksorisParkingTicketCashMachineDto[]>(
      IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateIksorisParkingTicketCashMachineRequest>,
  ): Promise<CreateResult<IksorisParkingTicketCashMachineDto>> => {
    const tscan = await post<
      CreateIksorisParkingTicketCashMachineRequest[],
      IksorisParkingTicketCashMachineDto[]
    >(IKSORIS_PARKING_TICKET_CASH_MACHINES_URL, [data])
    return {
      data: tscan[0],
    }
  },
  update: async (
    resource: string,
    {
      id,
      data,
      previousData,
    }: UpdateParams<IksorisParkingTicketCashMachineDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      IksorisParkingTicketCashMachineDto
    >(IKSORIS_PARKING_TICKET_CASH_MACHINES_URL, {
      id,
      name: data.name !== previousData.name ? data.name : undefined,
      isWatched:
        data.isWatched !== previousData.isWatched ? data.isWatched : undefined,
      categoryId:
        data.categoryId !== previousData.categoryId
          ? data.categoryId
          : undefined,
      cameraId:
        data.cameraId !== previousData.cameraId ? data.cameraId : undefined,
      address: data.address !== previousData.address ? data.address : undefined,
      automatAPIAddress:
        data.automatAPIAddress !== previousData.automatAPIAddress
          ? data.automatAPIAddress
          : undefined,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<IksorisParkingTicketCashMachineDto>(
      IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
      `/${id}`,
    )
    return Promise.resolve({ data: deleted })
  },
  getMoneyState: async (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ): Promise<CacheableDataProviderExtensionResult<GetMoneyStateResponse>> => {
    const moneyState = await get<GetMoneyStateResponse>(
      IKSORIS_PARKING_TICKET_CASH_MACHINES_URL,
      `/GetMoneyState/${params?.id}`,
    )
    return {
      data: moneyState,
    }
  },
  serviceCashPayment: async (
    resource: string,
    params: ServiceCashPaymentRequest,
  ): Promise<CacheableDataProviderExtensionResult<TransactionResponse>> => {
    const response = await post<ServiceCashPaymentRequest, TransactionResponse>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/ServiceCashPayment`,
      params,
    )
    return Promise.resolve({ data: response })
  },
  serviceCashFloat: async (
    resource: string,
    params: ServiceCashFloatRequest,
  ): Promise<CacheableDataProviderExtensionResult<TransactionResponse>> => {
    const response = await post<ServiceCashFloatRequest, TransactionResponse>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/ServiceCashFloat`,
      params,
    )
    return {
      data: response,
    }
  },
  serviceCashEmptying: async (
    resource: string,
    params: ServiceCashEmptyingRequest,
  ): Promise<CacheableDataProviderExtensionResult<TransactionResponse>> => {
    const response = await post<
      ServiceCashEmptyingRequest,
      TransactionResponse
    >(`${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/ServiceCashEmptying`, params)
    return {
      data: response,
    }
  },
  cancelTransaction: async (
    resource: string,
    params: CancelTransactionRequest,
  ): Promise<CacheableDataProviderExtensionResult<Record>> => {
    await post<CancelTransactionRequest, undefined>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/CancelTransaction`,
      params,
    )
    return Promise.resolve({ data: { id: params.internalTransactionId } })
  },
  finishServicePaymentPartially: async (
    resource: string,
    params: FinishServicePaymentPartiallyRequest,
  ): Promise<CacheableDataProviderExtensionResult<Record>> => {
    await post<FinishServicePaymentPartiallyRequest, undefined>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/FinishServicePaymentPartially`,
      params,
    )
    return Promise.resolve({ data: { id: params.internalTransactionId } })
  },
  setAPIKey: async (
    resource: string,
    params: SetAPIKeyDto,
  ): Promise<CacheableDataProviderExtensionResult<SetAPIKeyDto>> => {
    const response = await put<SetAPIKeyDto, SetAPIKeyDto>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/SetAPIKey`,
      params,
    )
    return {
      data: response,
    }
  },
  removeAPIKey: async (
    resource: string,
    params: RemoveAPIKeyDto,
  ): Promise<CacheableDataProviderExtensionResult<RemoveAPIKeyDto>> => {
    const response = await put<RemoveAPIKeyDto, RemoveAPIKeyDto>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/RemoveAPIKey`,
      params,
    )
    return {
      data: response,
    }
  },
  setAutomatAPICredentials: async (
    resource: string,
    params: SetCredentialsDto,
  ): Promise<CacheableDataProviderExtensionResult<SetCredentialsDto>> => {
    const response = await put<SetCredentialsDto, SetCredentialsDto>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/SetAutomatAPICredentials`,
      params,
    )
    return {
      data: response,
    }
  },
  removeAutomatAPICredentials: async (
    resource: string,
    params: RemoveCredentialsDto,
  ): Promise<CacheableDataProviderExtensionResult<RemoveCredentialsDto>> => {
    const response = await put<RemoveCredentialsDto, RemoveCredentialsDto>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/RemoveAutomatAPICredentials`,
      params,
    )
    return {
      data: response,
    }
  },
  restart: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const reseted = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/Restart/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: reseted })
  },
  lock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const locked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/Lock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: locked })
  },
  unlock: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const unlocked = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/Unlock/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: unlocked })
  },
  rebootDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const rebooted = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/RebootDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: rebooted })
  },
  shutDownDevice: async (
    resource: string,
    { ticketMachineId }: TicketMachineIdParams,
  ): Promise<any> => {
    const shutDown = await put<TicketMachineIdRequest, TicketMachineIdRequest>(
      `${IKSORIS_PARKING_TICKET_CASH_MACHINES_URL}/ShutDownDevice/`,
      { ticketMachineId },
    )
    return Promise.resolve({ data: shutDown })
  },
} as IksorisParkingTicketCashMachinesProvider

interface IksorisParkingTicketCashMachinesProvider extends DataProvider {
  getMoneyState: (
    resource: string,
    params: {
      readonly id: Identifier
    },
  ) => Promise<CacheableDataProviderExtensionResult<GetMoneyStateResponse>>

  serviceCashPayment: (
    resource: string,
    params: ServiceCashPaymentRequest,
  ) => Promise<CacheableDataProviderExtensionResult<TransactionResponse>>

  serviceCashFloat: (
    resource: string,
    params: ServiceCashFloatRequest,
  ) => Promise<CacheableDataProviderExtensionResult<TransactionResponse>>

  serviceCashEmptying: (
    resource: string,
    params: ServiceCashEmptyingRequest,
  ) => Promise<CacheableDataProviderExtensionResult<TransactionResponse>>

  cancelTransaction: (
    resource: string,
    params: CancelTransactionRequest,
  ) => Promise<CacheableDataProviderExtensionResult<Record>>

  finishServicePaymentPartially: (
    resource: string,
    params: FinishServicePaymentPartiallyRequest,
  ) => Promise<CacheableDataProviderExtensionResult<Record>>

  setAPIKey: (
    resource: string,
    params: SetAPIKeyDto,
  ) => Promise<CacheableDataProviderExtensionResult<SetAPIKeyDto>>

  removeAPIKey: (
    resource: string,
    params: RemoveAPIKeyDto,
  ) => Promise<CacheableDataProviderExtensionResult<RemoveAPIKeyDto>>

  setAutomatAPICredentials: (
    resource: string,
    params: SetCredentialsDto,
  ) => Promise<CacheableDataProviderExtensionResult<SetCredentialsDto>>

  removeAutomatAPICredentials: (
    resource: string,
    params: RemoveCredentialsDto,
  ) => Promise<CacheableDataProviderExtensionResult<RemoveCredentialsDto>>
  restart: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  lock: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  unlock: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  rebootDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
  shutDownDevice: (
    resource: string,
    params: TicketMachineIdParams,
  ) => Promise<CacheableDataProviderExtensionResult<TicketMachineIdParams>>
}

export interface CreateIksorisParkingTicketCashMachineRequest {
  readonly name: string
  readonly isWatched: string
  readonly categoryId: number
  readonly cameraId: number
  readonly address: string
  readonly automatAPIAddress: string
}

export interface TicketMachineIdParams {
  readonly ticketMachineId: number
}

export interface TicketMachineIdRequest {
  readonly ticketMachineId: Identifier
}

export default provider
